import React from "react";
import { Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { HelpProvider } from "./context/HelpContext";
import { SettingsProvider, useSettings } from "./context/SettingsContext";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import WhatsAppHelp from "./components/WhatsAppHelp";
import ScrollToTop from "./components/ScrollToTop";
import CookieConsent from "./components/CookieConsent";
import SEOTracking from "./components/SEOTracking";
import { getPublicPath } from "./utils/seoPages";
import useNativeBackButton from "./hooks/useNativeBackButton";
import useNativeExternalLinks from "./hooks/useNativeExternalLinks";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Catalog from "./pages/Catalog";
import CourseDetail from "./pages/CourseDetail";
import PackDetail from "./pages/PackDetail";
import Checkout from "./pages/Checkout";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import Dashboard from "./pages/Dashboard";
import Messages from "./pages/Messages";
import Notifications from "./pages/Notifications";
import CoursePlayer from "./pages/CoursePlayer";
import CommunityLanding from "./pages/CommunityLanding";
import { Community, ThreadDetail } from "./pages/Community";
import Affiliate from "./pages/Affiliate";
import DiagnosticStrategique from "./pages/DiagnosticStrategique";
import DiagnosticConfirme from "./pages/DiagnosticConfirme";
import AuthCallback from "./pages/AuthCallback";

import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCoursesList from "./pages/admin/AdminCoursesList";
import AdminCourseEditor from "./pages/admin/AdminCourseEditor";
import AdminAssignments from "./pages/admin/AdminAssignments";
import AdminAffiliates from "./pages/admin/AdminAffiliates";
import AdminStudents from "./pages/admin/AdminStudents";
import AdminPacks from "./pages/admin/AdminPacks";
import AdminPromo from "./pages/admin/AdminPromo";
import AdminBlogList from "./pages/admin/AdminBlogList";
import AdminBlogEditor from "./pages/admin/AdminBlogEditor";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminEmails from "./pages/admin/AdminEmails";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminStats from "./pages/admin/AdminStats";
import AdminTeam from "./pages/admin/AdminTeam";
import AdminSystemLogs from "./pages/admin/AdminSystemLogs";

import Blog from "./pages/Blog";
import BlogPostDetail from "./pages/BlogPostDetail";
import LegalPage from "./pages/legal/LegalPage";

function AppRouter() {
  const { settings } = useSettings();
  const catalogPath = getPublicPath(settings, "/catalog");
  const blogPath = getPublicPath(settings, "/blog");
  const affiliatePath = getPublicPath(settings, "/affiliate");
  const packsPath = getPublicPath(settings, "/packs");
  const diagnosticPath = getPublicPath(settings, "/diagnostic-strategique");
  const mentionsPath = getPublicPath(settings, "/mentions-legales");
  const privacyPath = getPublicPath(settings, "/politique-confidentialite");
  const cgvPath = getPublicPath(settings, "/conditions-generales-de-vente");

  // Bouton/geste retour d'Android : revenir a l'ecran precedent au lieu de
  // fermer l'application (sans effet sur le web et sur iOS).
  useNativeBackButton();
  // Liens "nouvel onglet" : ouverture dans le navigateur integre, sans quitter l'app.
  useNativeExternalLinks();

  return (
    <>
      <SEOTracking />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout><Landing /></Layout>} />
        <Route path="/login" element={<Layout><Login /></Layout>} />
        <Route path="/register" element={<Layout><Register /></Layout>} />
        <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
        <Route path="/reset-password" element={<Layout><ResetPassword /></Layout>} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        <Route path={catalogPath} element={<Layout><Catalog /></Layout>} />
        <Route path="/catalog" element={<Layout><Catalog /></Layout>} />
        <Route path={packsPath} element={<Layout><Catalog /></Layout>} />
        <Route path="/formations/:courseId" element={<Layout><CourseDetail /></Layout>} />
        <Route path="/courses/:courseId" element={<Layout><CourseDetail /></Layout>} />
        <Route path={`${packsPath}/:packId`} element={<Layout><PackDetail /></Layout>} />
        <Route path="/packs/:packId" element={<Layout><PackDetail /></Layout>} />

        <Route path="/checkout/:courseId" element={<Layout><ProtectedRoute><Checkout /></ProtectedRoute></Layout>} />
        <Route path="/checkout/pack/:packId" element={<Layout><ProtectedRoute><Checkout /></ProtectedRoute></Layout>} />
        <Route path="/checkout/success" element={<Layout><CheckoutSuccess /></Layout>} />

        <Route path="/dashboard" element={<Layout><ProtectedRoute><Dashboard /></ProtectedRoute></Layout>} />
        <Route path="/messages" element={<Layout><ProtectedRoute requireFormation><Messages /></ProtectedRoute></Layout>} />
        <Route path="/notifications" element={<Layout><ProtectedRoute><Notifications /></ProtectedRoute></Layout>} />
        <Route path="/learn/:lessonId" element={<Layout><ProtectedRoute><CoursePlayer /></ProtectedRoute></Layout>} />

        <Route path="/community" element={<Layout><CommunityLanding /></Layout>} />
        <Route path="/community/app" element={<Layout><ProtectedRoute requireFormation><Community /></ProtectedRoute></Layout>} />
        <Route path="/community/app/threads/:threadId" element={<Layout><ProtectedRoute requireFormation><ThreadDetail /></ProtectedRoute></Layout>} />
        <Route path="/community/threads/:threadId" element={<Layout><ProtectedRoute requireFormation><ThreadDetail /></ProtectedRoute></Layout>} />

        <Route path={blogPath} element={<Layout><Blog /></Layout>} />
        <Route path="/blog" element={<Layout><Blog /></Layout>} />
        <Route path="/blog/:slug" element={<Layout><BlogPostDetail /></Layout>} />

        <Route path={affiliatePath} element={<Layout><Affiliate /></Layout>} />
        <Route path="/affiliate" element={<Layout><Affiliate /></Layout>} />

        <Route path={diagnosticPath} element={<Layout><DiagnosticStrategique /></Layout>} />
        <Route path="/diagnostic-strategique" element={<Layout><DiagnosticStrategique /></Layout>} />
        <Route path="/diagnostic-confirme" element={<Layout><DiagnosticConfirme /></Layout>} />

        <Route path={mentionsPath} element={<Layout><LegalPage type="mentions-legales" /></Layout>} />
        <Route path="/mentions-legales" element={<Layout><LegalPage type="mentions-legales" /></Layout>} />
        <Route path={privacyPath} element={<Layout><LegalPage type="politique-confidentialite" /></Layout>} />
        <Route path="/politique-confidentialite" element={<Layout><LegalPage type="politique-confidentialite" /></Layout>} />
        <Route path={cgvPath} element={<Layout><LegalPage type="conditions-generales-de-vente" /></Layout>} />
        <Route path="/conditions-generales-de-vente" element={<Layout><LegalPage type="conditions-generales-de-vente" /></Layout>} />

        <Route path="/admin" element={<Layout><ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute></Layout>} />
        <Route path="/admin/courses" element={<Layout><ProtectedRoute adminOnly><AdminCoursesList /></ProtectedRoute></Layout>} />
        <Route path="/admin/courses/:courseId" element={<Layout><ProtectedRoute adminOnly><AdminCourseEditor /></ProtectedRoute></Layout>} />
        <Route path="/admin/packs" element={<Layout><ProtectedRoute adminOnly><AdminPacks /></ProtectedRoute></Layout>} />
        <Route path="/admin/promo" element={<Layout><ProtectedRoute adminOnly><AdminPromo /></ProtectedRoute></Layout>} />
        <Route path="/admin/blog" element={<Layout><ProtectedRoute adminOnly><AdminBlogList /></ProtectedRoute></Layout>} />
        <Route path="/admin/blog/:postId" element={<Layout><ProtectedRoute adminOnly><AdminBlogEditor /></ProtectedRoute></Layout>} />
        <Route path="/admin/settings" element={<Layout><ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute></Layout>} />
        <Route path="/admin/emails" element={<Layout><ProtectedRoute adminOnly><AdminEmails /></ProtectedRoute></Layout>} />
        <Route path="/admin/testimonials" element={<Layout><ProtectedRoute adminOnly><AdminTestimonials /></ProtectedRoute></Layout>} />
        <Route path="/admin/assignments" element={<Layout><ProtectedRoute adminOnly><AdminAssignments /></ProtectedRoute></Layout>} />
        <Route path="/admin/affiliates" element={<Layout><ProtectedRoute adminOnly><AdminAffiliates /></ProtectedRoute></Layout>} />
        <Route path="/admin/students" element={<Layout><ProtectedRoute adminOnly><AdminStudents /></ProtectedRoute></Layout>} />
        <Route path="/admin/stats" element={<Layout><ProtectedRoute adminOnly><AdminStats /></ProtectedRoute></Layout>} />
        <Route path="/admin/team" element={<Layout><ProtectedRoute adminOnly><AdminTeam /></ProtectedRoute></Layout>} />
        <Route path="/admin/system" element={<Layout><ProtectedRoute adminOnly><AdminSystemLogs /></ProtectedRoute></Layout>} />

        <Route path="*" element={<Layout><div className="max-w-2xl mx-auto p-12 text-center"><h1 className="font-heading text-4xl font-black">404</h1><p className="text-gray-600 mt-2">Page introuvable.</p></div></Layout>} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <HelpProvider>
          <AppRouter />
          <WhatsAppHelp />
          <CookieConsent />
        </HelpProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}
